import React, { useEffect, useRef, useState } from 'react';
import './Atlas.css';

const Atlas = ({ onBack }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const detailRef = useRef(null);
  const tooltipRef = useRef(null);
  const focusedNodeRef = useRef(null);
  const [activeTab, setActiveTab] = useState('description');
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasContainer = containerRef.current;
    const detailPanel = detailRef.current;
    const tooltip = tooltipRef.current;
    if (!canvas || !canvasContainer || !detailPanel || !tooltip) return;

    const ctx = canvas.getContext('2d');

    // ── Contenu JS vanilla adapté ────────────────────────────
// canvas = canvasRef.current (déjà défini)
// ctx déjà défini
// canvasContainer = containerRef.current (déjà défini)
// tooltip = tooltipRef.current (déjà défini)
// detailPanel = detailRef.current (déjà défini)

// Resize
function resizeCanvas() {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
}

window.addEventListener("resize", resizeCanvas);
resizeCanvas();

// Données des nœuds
const categories = {
  participants: { color: "#00ffff", name: "Participant·e" },
  ateliers: { color: "#b794f6", name: "Atelier" },
  créations: { color: "#ff6b9d", name: "Création" },
};

// Système de gestion des images PNG
const nodeImages = {};
const imageLoadingStatus = {};
const imageDimensions = {};

function loadNodeImage(nodeId, imagePath) {
  if (!nodeImages[nodeId]) {
    const img = new Image();
    img.onload = () => {
      imageLoadingStatus[nodeId] = "loaded";
      imageDimensions[nodeId] = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
      };
    };
    img.onerror = () => {
      imageLoadingStatus[nodeId] = "error";
    };
    imageLoadingStatus[nodeId] = "loading";
    img.src = imagePath;
    nodeImages[nodeId] = img;
  }
}

function getImageDisplayDimensions(node) {
  if (!node.imageUrl || !imageDimensions[node.id]) return null;
  const baseSize = node.imageSize || (node.isMother ? 70 : 50);
  const aspectRatio = imageDimensions[node.id].aspectRatio;
  let width, height;
  if (aspectRatio > 1) {
    width = baseSize;
    height = baseSize / aspectRatio;
  } else {
    height = baseSize;
    width = baseSize * aspectRatio;
  }
  return { width, height };
}

// Système de zoom animé
const nodeZoomStates = {}; // Stocke le zoom actuel de chaque nœud
const ZOOM_SPEED = 0.15; // Vitesse de transition (0.1 = lent, 0.3 = rapide)

// Configuration des niveaux de zoom
const ZOOM_HOVER = 2; // Zoom au survol (hover) - léger agrandissement
const ZOOM_FOCUS = 3; // Zoom au clic (focus) - très gros agrandissement

function updateNodeZoom(node, targetZoom) {
  if (!nodeZoomStates[node.id]) {
    nodeZoomStates[node.id] = 1; // Zoom initial = 1
  }
  // Interpolation smooth vers le zoom cible
  nodeZoomStates[node.id] +=
    (targetZoom - nodeZoomStates[node.id]) * ZOOM_SPEED;
  return nodeZoomStates[node.id];
}

const nodes = [
  {
    id: 1,
    title: "Elise",
    category: "participants",
    isMother: false,
    x: 450,
    y: 200,
    vx: 0,
    vy: 0,
    connections: [],

    // ========== EXEMPLE DE CONTENU RICHE ==========
    // Vous pouvez copier cette structure pour les autres nœuds

    // description: `
    //   <p>Élise est une participante active du projet Fiction & Mondes multiples.</p>
    //   <h4>Centres d'intérêt</h4>
    //   <ul>
    //     <li>Design interactif</li>
    //     <li>Mondes virtuels</li>
    //     <li>Narration transmedia</li>
    //   </ul>
    // `,

    // Images (optionnel) - Supprimer si pas d'images
    // detailImages: [
    //   {
    //     src: "/media/APA/images/participants/elise.jpg",
    //     alt: "Portrait d'Élise",
    //     caption: "Élise lors de l'atelier TouchDesigner",
    //     orientation: "portrait"  // "portrait" ou "landscape"
    //   }
    // ],

    // Vidéos (optionnel) - Supprimer si pas de vidéos
    // detailVideos: [
    //   {
    //     src: "videos/elise-demo.mp4",
    //     type: "video/mp4",
    //     caption: "Démonstration du projet",
    //     orientation: "landscape"
    //   }
    // ]
  },
  {
    id: 2,
    title: "Joséphine",
    category: "participants",
    isMother: false,
    x: 450,
    y: 200,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",
  },
  {
    id: 3,
    title: "Gaspard",
    category: "participants",
    isMother: false,
    x: 450,
    y: 200,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",
  },
  {
    id: 4,
    title: "Léonie",
    category: "participants",
    isMother: false,
    x: 450,
    y: 200,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",
  },
  {
    id: 5,
    title: "Nassim",
    category: "participants",
    isMother: false,
    x: 450,
    y: 200,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",
  },
  {
    id: 6,
    title: "Atelier TouchDesigner",
    category: "ateliers",
    isMother: false,
    x: 600,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",
    //imageUrl: "/media/APA/images/Screenshot_21.jpg",
    //imageSize: 60,
    //imageInSphere: true, // L'image remplit le cercle (auto-rognée)

    detailImages: [
      {
        src: "/media/APA/images/TouchDesigner1.png",
        // alt: "Portrait d'Élise",
        // caption: "Élise lors de l'atelier TouchDesigner",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>TouchDesigner, développé par Derivative, est un logiciel de création, de diffusion et d’expérimentation audiovisuelle. 
      <br><br>La force de TouchDesigner réside dans la facilité de coupler son utilisation avec celle d’autres logiciels de création et de diffusion ainsi que dans son traitement des données, du mixage et du montage en temps réel : on peut y intégrer de la 2D, de la 3D, l’utiliser pour des protocoles standards de traitement du son et de la lumière, pour créer des passerelles avec des capteurs numériques, etc. 
      <br>Il devient rapidement un outil de référence pour des artistes, des programmeur·euses, des codeur·euses créatif·ves, des concepteur·ices de logiciels et des interprètes du spectacle vivant pour créer des performances, des installations et des œuvres multimédias - interactives ou pas.
      <br><br>TouchDesigner repose sur un fonctionnement de programmation nodale - l’utilisateur·ice crée des noeuds entre des modules intéragissant entre eux afin de transformer et d’agir sur un signal (visuel, numérique, sonore, etc). 
      <br>Ces opérateurs sont connectés ensemble, ou mis en chaîne, comme un réseau racinaire. Le signal traité se transforme d’opérateur en opérateur.
      <br><br></p>
      <img src="/media/APA/images/Screenshot_21.jpg" 
         alt="Description" 
         style="width: 100%; border-radius: 4px; margin: 1rem 0;">
      <img src="/media/APA/images/Screenshot_39.jpg" 
         alt="Description" 
         style="width: 100%; border-radius: 4px; margin: 1rem 0;">
    `,
  },
  {
    id: 7,
    title: "Atelier Surréalisme",
    category: "ateliers",
    isMother: false,
    x: 600,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/cadavre_exquis.jpg",
        alt: "Cadavre Exquis",
        caption:
          "Cadavre Exquis - André Breton, Jacqueline Lamba, Yves Tanguy - dessin, collage de gravures découpées sur papier - 7 février 1938",
        orientation: "portrait", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>En reprenant des protocoles d'écriture et de dessin automatique abordés par les artistes surréalistes, dadaïstes ou les artistes Oulipo, nous avons mis en place des séances de création surréaliste. 
      <br>En nous basant sur des contraintes hasardeuses et aléatoires et en passant nos créations de mains en mains, le but était de souder et décomplexer le groupe - de débloquer l'acte créatif instantané. 
      <br>C'était aussi une entrée en matière sur la question de la fiction. Les résultats sont venus nourrir une banque de données à exploiter dans d'autres ateliers, notamment celui sur l'utilisation de l'intelligence artificielle.</p>
    `,
  },
  {
    id: 8,
    title: "Atelier I.A.",
    category: "ateliers",
    isMother: false,
    x: 550,
    y: 500,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/image (62).png",
        // alt: "Cadavre Exquis",
        caption: "d'après des prompts de création personnelle - Elise Voët",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>Au coeur des enjeux actuels dans le domaine de la création et bien au-delà, la question de l'utilisation de l'I.A. est évidemment abordée à l'ESAD.
      <br>À partir de créations personelles, des résultats de l'atelier Surréalisme et d'autres médias, nous avons constitué des prompts d'images pour modeler de nouvelles formes visuelles inédites.
        <br><br><li>Un prompt est une commande. Elle peut se faire par des informations textuelles mais aussi par une sélection d'images.</li>
      <img src="/media/APA/images/Prompts_Page_1.jpg" 
         alt="Description" 
         style="width: 100%; border-radius: 4px; margin: 1rem 0;">
      <br><br>Cet atelier a eu la vocation d'ouvrir la conversation sur des questions éthiques dans la démocratisation de cet outil et de présenter les oeuvres d'artistes approchant l'utilisation de l'intelligence artificielle d'un point de vue critique. 
      <br>Quelles sont les répercussions ? Quel est le coût ? Qu'est ce que cela veut dire d'utiliser l'I.A. dans la création artistique contemporaine ?
      <br><br>Avec une pédagogie discréditant l'utilisation à outrance de l'I.A., en particulier celle des agents conversationnels dont l'assistance rompt avec la prise d'action et la débrouillardise, l'atelier s'est basé sur la prise en main d'outils de génération visuelle assistée par I.A. mais en local. L'agent est hébergé en local sur l'ordinateur plutôt que depuis un serveur en ligne dans le but de réduire notre empreinte et d'aborder cet outil de façon éthique et consciencieuse d'un point de vue écologique.
      <br><br>Les artistes ont sû s'emparer des nouvelles technologies depuis toujours afin de déconstruire ces nouveaux outils et de générer de l'inattendu. Il en va de même pour l'intelligence artificielle. 
      <br>À l'issue de cet ateliers, les participant·es ont également pris conscience des limitations inhérentes à cette technique de création.
      </p>`,
  },
  {
    id: 9,
    title: "Séance cinéma",
    category: "ateliers",
    isMother: false,
    x: 350,
    y: 100,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/Hito.jpg",
        // alt: "Cadavre Exquis",
        caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>Quelques séances de visionnage ou d'écoute sont organisées dans l'année pour étendre la culture, détendre l'atmosphère et passer un bon moment ensemble autour d'un bol de pop-corn.
      <br>La dernière séance cinéma nous permettait de voir et de comparer quelques courts métrages ayant fait partie des sélections du festival les Utopiales de Nantes.
      <br>Tout cela dans le but d'initier une vision critique du montage et de la composition d'un court métrage en préparation de l'atelier Montage vidéo.
      <br><br></p>
            <ul>
        <li>ELECTRIC BODIES - Les corps électriques
            <br>Antoine Janot
            <br>2021
            <br>In a future where biological bodies are replaced by biomechanical ones, anyone can change their appearance according to their mood, as long as they can afford it. But those who can’t pay the rent of their expensive bodies are forced to abandon them.
        </li>
        <br>
        <li>À LA MODE
            <br>Jean Lecointre
            <br>2020
            <br>Once upon a time, beyond the mountains far away, there was a kingdom tormented by a terrible anguish of no longer being fashionable.
        </li>
                <br>
        <li>FACELIFT
            <br>Virat Pal
            <br>2022
            <br>Things start to change in the life of a middle-aged woman when a mysterious mask appears on her doorstep. 
        </li>
                <br>
        <li>MYSTIC TIGER
            <br>Marc Martínez Jordán
            <br>2022
            <br>After suffering a mysterious accident, a man acquires the ability to transgress time/space. However, the unbridled use of this gift will inevitably lead him to discover the true reason behind his power.
        </li>
                <br>
        <li>HITO
            <br>Stephen Lopez
            <br>2023
            <br>Between nuclear reactors and military curfews, 14-year-old Jani lives in a dystopian world oppressively devoid of empathy. Together, she and her slippery new friend Kiefer the talking catfish gear up to strike a surreal blow for freedom.
        </li>
      </ul>

      </p>`,
  },
  {
    id: 10,
    title: "Atelier Montage vidéo",
    category: "ateliers",
    isMother: false,
    x: 200,
    y: 400,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/DaVinci_Resolve.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>Fiction & Mondes multiples cultive l'utilisation de logiciels libres ou accessibles gratuitement.
      <br>Exercice pour s'initier à la création vidéo, cet atelier visait à prendre en main le logiciel DaVinci Resolve - référence en terme d'outil de montage vidéo professionnel et accessible.
      <br><br>L'atelier est encore en court et vise à étendre le pannel de compétences des participant·es dans le but de constituer un projet pour la fin d'année scolaire.
      </p>`,
  },
  {
    id: 11,
    title: "Atelier Mixage sonore",
    category: "ateliers",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [12],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/Reaper.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>Fiction & Mondes multiples cultive l'utilisation de logiciels libres ou accessibles gratuitement.
      <br>Exercice pour s'initier à la création sonore, cet atelier visait à prendre en main le logiciel Reaper - référence en terme d'outil de mixage et de composition professionnel et accessible.
      <br><br>L'atelier est encore en court et vise à étendre le pannel de compétences des participant·es dans le but de constituer un projet pour la fin d'année scolaire.
      </p>`,
  },
  {
    id: 12,
    title: "Atelier Spatialisation sonore",
    category: "ateliers",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [],
    description: "",

    detailImages: [
      {
        src: "/media/APA/images/Screenshot_28.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],

    description: `
      <p>Après une introduction à la composition sonore, les projets émergents de cet atelier sont recomposés dans un espace octophonique (huit enceintes).
      <br>L’audio multicanal consiste à enregistrer et diffuser le son sur plusieurs pistes et haut-parleurs distincts pour créer une expérience sonore plus immersive.
      <br>Contrairement à la stéréo qui utilise deux canaux (gauche et droit), l’audio multicanal utilise au moins trois canaux, souvent plus, afin de spatialiser le son autour de l’auditeur.</p>
      <br><br><h4>Ecoutez SILOPORTEM - Weaving the Heart Machine, une création audiovisuelle spatialisée sur huit enceintes !
      <br>Diffusion sur la mezzanine de l'Abreuvoir toutes les heures à partir de 10h30 !</h4>
      <br><br>L'atelier est encore en court et vise à étendre le pannel de compétences des participant·es dans le but de constituer un projet pour la fin d'année scolaire.
      </p>`,
  },
  {
    id: 13,
    title: "", //TD_Leonie_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 4, 14, 15, 16],
    description: "",
    imageUrl: "/media/APA/images/TD_Leonie_1_min.png",
    imageSize: 60,

    detailVideos: [
      {
        src: "/media/APA/images/TD_Leonie_1.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
    ],
  },
  {
    id: 14,
    title: "", //TD_Gaspard_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 3, 13, 15, 16],
    description: "",
    imageUrl: "/media/APA/images/TD_Gaspard_1_min.png",
    imageSize: 60,

    detailVideos: [
      {
        src: "/media/APA/images/TD_Gaspard_1.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
    ],
  },
  {
    id: 15,
    title: "", //TD_Nassim_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 5, 13, 14, 16],
    description: "",
    imageUrl: "/media/APA/images/TD_Nassim_1_min.png",
    imageSize: 60,

    detailVideos: [
      {
        src: "/media/APA/images/TD_Nassim_1.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
      {
        src: "/media/APA/images/TD_Nassim_2.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
    ],
  },
  {
    id: 16,
    title: "", //TD_Elise_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 1, 13, 14, 15],
    description: "",
    imageUrl: "/media/APA/images/TD_Elise_1_min.png",
    imageSize: 60,

    detailVideos: [
      {
        src: "/media/APA/images/TD_Elise_1.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
    ],
  },
  {
    id: 17,
    title: "", //FMM_10_11_25_Gaspard1_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 3, 18],
    description: "",
    imageUrl: "/media/APA/images/FMM_10_11_25_Gaspard1_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)

    detailImages: [
      {
        src: "/media/APA/images/FMM_10_11_25_Gaspard1.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 18,
    title: "", //FMM_10_11_25_Gaspard2_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 3, 17],
    description: "",
    imageUrl: "/media/APA/images/FMM_10_11_25_Gaspard2_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_10_11_25_Gaspard2.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 19,
    title: "", //FMM_10_11_25_Josephine1_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 2, 20],
    description: "",
    imageUrl:
      "/media/APA/images/FMM_10_11_25_Josephine1_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_10_11_25_Josephine1.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 20,
    title: "", //FMM_10_11_25_Josephine2_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 2, 19],
    description: "",
    imageUrl:
      "/media/APA/images/FMM_10_11_25_Josephine2_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_10_11_25_Josephine2.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 21,
    title: "", //FMM_10_11_25_Leonie1_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 2],
    description: "",
    imageUrl: "/media/APA/images/FMM_10_11_25_Leonie1_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_10_11_25_Leonie1.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 22,
    title: "", //Cadavre Excquis
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 1, 2, 3, 4, 5],
    description: "",
    imageUrl: "/media/APA/images/slide4_1_min.png",
    imageSize: 120,

    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_CE_slide1.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/FMM_17_11_25_CE_slide2.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/FMM_17_11_25_CE_slide3.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/FMM_17_11_25_CE_slide4.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 23,
    title: "", //FMM_17_11_25_Elise_1_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 1, 27, 28],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Elise_1_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Elise_1.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // CORRIGÉ : "landscape" au lieu de "landscape"
        // Note : Pour agrandir l'image dans le panneau, utilisez du CSS inline dans la description
      },
    ],
  },
  {
    id: 26,
    title: "", //FMM_17_11_25_Elise3_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 1],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Elise3_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Elise3.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 27,
    title: "", //FMM_17_11_25_Elise4_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 1, 23, 28],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Elise4_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Elise4.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 28,
    title: "", //FMM_17_11_25_Elise5_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 1, 23, 27],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Elise5_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Elise5.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 29,
    title: "", //FMM_17_11_25_Gaspard1_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 3],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Gaspard1_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Gaspard1.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 30,
    title: "", //FMM_17_11_25_Gaspard2_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 3],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Gaspard2_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Gaspard2.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  //   {
  //     id: 31,
  //     title: "", //FMM_10_11_25_Leonie1_min
  //     category: "créations",
  //     isMother: false,
  //     x: 1050,
  //     y: 350,
  //     vx: 0,
  //     vy: 0,
  //     connections: [7, 4],
  //     description: "",
  //     imageUrl: "/media/APA/images/FMM_10_11_25_Leonie1_min.png",
  //     imageSize: 60,
  //     imageInSphere: true, // L'image remplit le cercle (auto-rognée)
  //     detailImages: [
  //       {
  //         src: "/media/APA/images/FMM_10_11_25_Leonie1.png",
  //         // alt: "Cadavre Exquis",
  //         // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
  //         orientation: "landscape", // "portrait" ou "landscape"
  //       },
  //     ],
  //   },
  {
    id: 32,
    title: "", //FMM_17_11_25_Leonie3_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 4, 33],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Leonie3_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Leonie3.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 33,
    title: "", //FMM_17_11_25_Leonie2_min
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 4, 32],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Leonie2_min.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Leonie2.png",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 34,
    title: "", //Trombi_Josephine_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 2, 35, 36],
    description: "",
    imageUrl: "/media/APA/images/Trombi_Josephine_1.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/Trombi_Josephine_1.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 35,
    title: "", //Trombi_Elise_2
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 1, , 34, 36],
    description: "",
    imageUrl: "/media/APA/images/Trombi_Elise_2.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/Trombi_Elise_2.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/Trombi_Elise_1.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 36,
    title: "", //Trombi_Elise_3
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [6, 1, 34, 35],
    description: "",
    imageUrl: "/media/APA/images/Trombi_Elise_3.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/Trombi_Elise_3.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/Trombi_Elise_4.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
  {
    id: 37,
    title: "", //video_nassim_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [10, 5],
    description: "",
    imageUrl: "/media/APA/images/video_nassim_1.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)

    detailVideos: [
      {
        src: "/media/APA/images/Video_Nassim.mp4",
        type: "video/mp4",
        // caption: "Démonstration du projet",
        orientation: "landscape",
      },
    ],
  },
  {
    id: 38,
    title: "", //IA_Nassim_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 5],
    description: "",
    imageUrl: "/media/APA/images/IA_Nassim_1.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/IA_Nassim_1.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issue d'une création personnelle</p>`,
  },
  {
    id: 39,
    title: "", //IA_Leonie_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 4, 43],
    description: "",
    imageUrl: "/media/APA/images/le cri.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/le cri.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/chat aillé.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images d'animaux issues de la BDNF
      </p>`,
  },
  {
    id: 40,
    title: "", //IA_Leonie_2
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 4],
    description: "",
    imageUrl: "/media/APA/images/jardin des délices.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/\jardin des délices.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/utopie magique.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images d'animaux issues de la BNF - déclinaisons de l'oeuvre le Jardin des délices, Jérôme Bosch
      </p>`,
  },
  {
    id: 41,
    title: "", ////IA_Leonie_3
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 4, 44, 45],
    description: "",
    imageUrl: "/media/APA/images/paysage yeux.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/paysage yeux.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images satellite de Google Earth
      </p>`,
  },
  {
    id: 42,
    title: "", //IA_Elise_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 46, 47],
    description: "",
    imageUrl: "/media/APA/images/Dissection_Elise.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/Dissection_Elise.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-27-48_4959.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-28-14_6653.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-28-28_5682.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-35-06_2303.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-35-19_4868.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-35-19_4868.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-35-45_9554.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues de la BNF
      </p>`,
  },
  {
    id: 43,
    title: "", //IA_Elise_2
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 39],
    description: "",
    imageUrl: "/media/APA/images/louton4.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/louton4.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/louton1.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/louton2.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/louton3.jpg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues de la BDNF
      </p>`,
  },
  {
    id: 44,
    title: "", //IA_Elise_3
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 41, 45],
    description: "",
    imageUrl: "/media/APA/images/paysage_toxique.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/paysage_toxique.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-40-55_2936.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-41-08_9168.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-40-30_3151.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images satellites issues de Google Earth
      </p>`,
  },
  {
    id: 45,
    title: "", //IA_Elise_4
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 41, 44],
    description: "",
    imageUrl: "/media/APA/images/flesh_paysage.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/flesh_paysage.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-43-37_5032.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images satellites issues de Google Earth
      </p>`,
  },
  {
    id: 46,
    title: "", //IA_Elise_5
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 42, 47],
    description: "",
    imageUrl: "/media/APA/images/blob_algae.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/blob_algae.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-41-31_8396.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-41-04_7851.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-40-51_7136.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues de la BNF
      </p>`,
  },
  {
    id: 47,
    title: "", //IA_Elise_6
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1, 42, 46, 48],
    description: "",
    imageUrl: "/media/APA/images/god_of_meat_elise.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/god_of_meat_elise.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-44-46_4610.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-44-20_9177.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-12-01_20-45-00_6005.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues de la BNF
      </p>`,
  },
  {
    id: 48,
    title: "", //IA_Gaspard_1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 3, 47],
    description: "",
    imageUrl: "/media/APA/images/god_of_meat.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/god_of_meat.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues d'une collection personnelle
      </p>`,
  },
  {
    id: 49,
    title: "", //IA_Elise_7
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 1],
    description: "",
    imageUrl: "/media/APA/images/sorcier_cyanotipe.jpeg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/sorcier_cyanotipe.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-54-41_1820.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-54-54_2364.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/2025-11-30_21-55-06_2171.jpeg",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues de la BNF
      </p>`,
  },
  {
    id: 50,
    title: "", //IA_Gaspard_2
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 3, 17, 18, 29, 30],
    description: "",
    imageUrl: "/media/APA/images/prompt_gaspard_1.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/prompt_gaspard_1.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_2.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_3.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_4.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues des dessins de Gaspard réalisés lors des ateliers Surréalisme
      </p>`,
  },
  {
    id: 51,
    title: "", //IA_Gaspard_3
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [8, 3, 17, 18, 29, 30],
    description: "",
    imageUrl: "/media/APA/images/prompt_gaspard_7.png",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/prompt_gaspard_7.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_5.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_6.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
      {
        src: "/media/APA/images/prompt_gaspard_8.png",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
    description: `
      <p>prompt d'images issues des dessins de Gaspard réalisés lors des ateliers Surréalisme
      </p>`,
  },
  {
    id: 52,
    title: "Composition_sonore_Leonie", //Mixage_Leonie
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [11, 4],
    description: "",
    imageSize: 60,
    detailAudios: [
      {
        src: "/media/APA/images/Mix_Leonie.mp3",
        //   caption: "Ambiance sonore - 2:30"
      },
    ],
  },
  {
    id: 53,
    title: "Composition_sonore_Gaspard", //Mixage_Gaspard
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [11, 3],
    description: "",
    imageSize: 60,
    detailAudios: [
      {
        src: "/media/APA/images/Mix_Gaspard.mp3",
        //   caption: "Ambiance sonore - 2:30"
      },
    ],
  },
  {
    id: 54,
    title: "Composition_sonore_Josephine", //Mixage_Gaspard
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [11, 2],
    description: "",
    imageSize: 60,
    detailAudios: [
      {
        src: "/media/APA/images/Mix_Josephine.mp3",
        //   caption: "Ambiance sonore - 2:30"
      },
    ],
  },
  {
    id: 55,
    title: "", //FMM_17_11_25_Josephine1
    category: "créations",
    isMother: false,
    x: 1050,
    y: 350,
    vx: 0,
    vy: 0,
    connections: [7, 2, 20],
    description: "",
    imageUrl: "/media/APA/images/FMM_17_11_25_Josephine1.jpg",
    imageSize: 60,
    imageInSphere: true, // L'image remplit le cercle (auto-rognée)
    detailImages: [
      {
        src: "/media/APA/images/FMM_17_11_25_Josephine1.jpg",
        // alt: "Cadavre Exquis",
        // caption: "extrait de Hito, Stephen Lopez, 2023 HITO",
        orientation: "landscape", // "portrait" ou "landscape"
      },
    ],
  },
];

// ==================== SYSTÈME DE POSITIONNEMENT ORGANIQUE ====================
// Positionne automatiquement les éléments en fonction de leurs connexions
function initializeOrganicLayout() {
  const positioned = new Set();
  const centerX = 600;
  const centerY = 350;

  // Trouver les nœuds "racines" (ceux qui ont des positions définies non-superposées)
  const roots = nodes.filter((node) => {
    return (
      (node.x !== 1050 || node.y !== 350) && (node.x !== 0 || node.y !== 0)
    );
  });

  // Marquer les racines comme positionnées
  roots.forEach((node) => positioned.add(node.id));

  // Fonction pour positionner un nœud autour d'un parent
  function positionAroundParent(node, parentNode, angleOffset = 0) {
    // Distance augmentée pour les ateliers pour mieux séparer les branches
    let distance = 150; // Distance de base

    // Si c'est un atelier, augmenter la distance
    if (node.category === "ateliers" || parentNode.category === "ateliers") {
      distance = 200; // Plus d'espace pour les branches d'ateliers
    }

    // Si c'est une création connectée à un atelier, encore plus d'espace
    if (node.category === "créations" && parentNode.category === "ateliers") {
      distance = 220; // Créations des ateliers encore plus espacées
    }

    const siblings = nodes.filter(
      (n) => n.connections.includes(parentNode.id) && !positioned.has(n.id)
    );

    const siblingIndex = siblings.findIndex((n) => n.id === node.id);
    const totalSiblings = siblings.length;

    // Répartir en cercle autour du parent
    const angleStep = (Math.PI * 2) / Math.max(totalSiblings, 3);
    const angle = angleOffset + siblingIndex * angleStep;

    node.x = parentNode.x + Math.cos(angle) * distance;
    node.y = parentNode.y + Math.sin(angle) * distance;

    positioned.add(node.id);
  }

  // Parcourir les nœuds non positionnés et les placer près de leurs connexions
  let iterations = 0;
  const maxIterations = 10;

  while (positioned.size < nodes.length && iterations < maxIterations) {
    iterations++;

    nodes.forEach((node) => {
      if (positioned.has(node.id)) return;

      // Trouver un parent déjà positionné
      const positionedParent = node.connections
        .map((id) => nodes.find((n) => n.id === id))
        .filter((n) => n && positioned.has(n.id))[0];

      if (positionedParent) {
        positionAroundParent(
          node,
          positionedParent,
          (Math.random() * Math.PI) / 4
        );
      } else {
        // Si pas de parent, placer aléatoirement près du centre
        const angle = Math.random() * Math.PI * 2;
        const distance = 200 + Math.random() * 100;
        node.x = centerX + Math.cos(angle) * distance;
        node.y = centerY + Math.sin(angle) * distance;
        positioned.add(node.id);
      }
    });
  }

  // Ajouter un petit décalage aléatoire pour éviter les superpositions exactes
  nodes.forEach((node) => {
    node.x += (Math.random() - 0.5) * 30;
    node.y += (Math.random() - 0.5) * 30;
  });
}

// Initialiser le layout organique
initializeOrganicLayout();

// Précharger les images
nodes.forEach((node) => {
  if (node.imageUrl) loadNodeImage(node.id, node.imageUrl);
});

// État de la vue
let scale = 1;
let offsetX = 0;
let offsetY = 0;
let isDraggingCanvas = false;
let lastMouseX = 0;
let lastMouseY = 0;
let hoveredNode = null;
let focusedNode = null;

// Simulation physique légère - AJUSTÉ pour meilleure séparation des branches
const DRIFT_STRENGTH = 0.00008; // Mouvement très subtil
const DAMPING = 0.98; // Amortissement
const REPULSION_RADIUS = 180; // Augmenté de 120 à 180 pour plus d'espace
const REPULSION_STRENGTH = 0.6; // Augmenté de 0.4 à 0.6 pour plus de répulsion
const CENTER_ATTRACTION = 0.00002; // Réduit de 0.0001 à 0.00002 pour éviter convergence au centre
const FORCE_FIELD_RADIUS = 300; // Rayon du champ de force au clic
const FORCE_FIELD_STRENGTH = 8; // Force de répulsion au clic

// Variables pour l'effet de champ de force
let forceFieldActive = false;
let forceFieldX = 0;
let forceFieldY = 0;
let forceFieldTimer = 0;
const FORCE_FIELD_DURATION = 800; // ms

// Variables pour le recentrage automatique
let lastInteractionTime = Date.now();
let autoRecenterActive = false;
let recenterStartTime = 0;
let recenterStartOffsetX = 0;
let recenterStartOffsetY = 0;
let recenterStartScale = 1;
const INACTIVITY_DELAY = 10000; // 10 secondes avant de commencer le recentrage
const RECENTER_DURATION = 20000; // 20 secondes pour le recentrage complet

// Appliquer la physique
function applyPhysics() {
  // Gestion du recentrage automatique
  const now = Date.now();
  const timeSinceInteraction = now - lastInteractionTime;

  // DÉSACTIVÉ : Le recentrage automatique fait converger tout vers le centre
  // Ce qui nuit à la séparation des branches et à la lecture des connexions
  /*
  if (timeSinceInteraction > INACTIVITY_DELAY && !autoRecenterActive) {
    // Démarrer le recentrage
    autoRecenterActive = true;
    recenterStartTime = now;
    recenterStartOffsetX = offsetX;
    recenterStartOffsetY = offsetY;
    recenterStartScale = scale;
  }

  if (autoRecenterActive) {
    const recenterProgress = Math.min(
      1,
      (now - recenterStartTime) / RECENTER_DURATION
    );

    // Fonction d'easing pour un mouvement très doux
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const easedProgress = easeInOutCubic(recenterProgress);

    // Calculer les limites du réseau
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    nodes.forEach((node) => {
      const radius = node.isMother ? 35 : 25;
      minX = Math.min(minX, node.x - radius - 50);
      maxX = Math.max(maxX, node.x + radius + 50);
      minY = Math.min(minY, node.y - radius - 50);
      maxY = Math.max(maxY, node.y + radius + 50);
    });

    const networkWidth = maxX - minX;
    const networkHeight = maxY - minY;
    const networkCenterX = (minX + maxX) / 2;
    const networkCenterY = (minY + maxY) / 2;

    // Calculer le zoom optimal (avec marge)
    const scaleX = (canvas.width * 0.85) / networkWidth;
    const scaleY = (canvas.height * 0.85) / networkHeight;
    const targetScale = Math.min(scaleX, scaleY, 3.5); // Max 1.2x zoom

    // Calculer l'offset pour centrer
    const targetOffsetX = canvas.width / 2 / targetScale - networkCenterX;
    const targetOffsetY = canvas.height / 2 / targetScale - networkCenterY;

    // Interpoler progressivement
    scale =
      recenterStartScale + (targetScale - recenterStartScale) * easedProgress;
    offsetX =
      recenterStartOffsetX +
      (targetOffsetX - recenterStartOffsetX) * easedProgress;
    offsetY =
      recenterStartOffsetY +
      (targetOffsetY - recenterStartOffsetY) * easedProgress;

    if (recenterProgress >= 1) {
      autoRecenterActive = false;
    }
  }
  */

  const centerX = canvas.width / 2 / scale - offsetX;
  const centerY = canvas.height / 2 / scale - offsetY;

  // Effet de champ de force temporaire
  if (forceFieldActive) {
    forceFieldTimer -= 16; // ~60fps
    if (forceFieldTimer <= 0) {
      forceFieldActive = false;
    }

    // Appliquer la force de répulsion
    const forceDecay = forceFieldTimer / FORCE_FIELD_DURATION;
    nodes.forEach((node) => {
      const dx = node.x - forceFieldX;
      const dy = node.y - forceFieldY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < FORCE_FIELD_RADIUS && dist > 0) {
        const force =
          FORCE_FIELD_STRENGTH * forceDecay * (1 - dist / FORCE_FIELD_RADIUS);
        node.vx += (dx / dist) * force;
        node.vy += (dy / dist) * force;
      }
    });
  }

  nodes.forEach((node, i) => {
    // Attraction très légère vers le centre (gravité)
    const toCenterX = centerX - node.x;
    const toCenterY = centerY - node.y;
    node.vx += toCenterX * CENTER_ATTRACTION;
    node.vy += toCenterY * CENTER_ATTRACTION;

    // Mouvement de dérive aléatoire (très subtil)
    node.vx += (Math.random() - 0.5) * DRIFT_STRENGTH;
    node.vy += (Math.random() - 0.5) * DRIFT_STRENGTH;

    // Répulsion entre nœuds
    nodes.forEach((other, j) => {
      if (i !== j) {
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < REPULSION_RADIUS && dist > 0) {
          const force =
            (REPULSION_STRENGTH * (1 - dist / REPULSION_RADIUS)) / dist;
          node.vx -= dx * force;
          node.vy -= dy * force;
        }
      }
    });

    // Amortissement
    node.vx *= DAMPING;
    node.vy *= DAMPING;

    // Limiter la vitesse
    const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
    const maxSpeed = 0.5;
    if (speed > maxSpeed) {
      node.vx = (node.vx / speed) * maxSpeed;
      node.vy = (node.vy / speed) * maxSpeed;
    }

    // Appliquer la vitesse
    node.x += node.vx;
    node.y += node.vy;
  });
}

// Conversion coordonnées
function toWorld(screenX, screenY) {
  return {
    x: screenX / scale - offsetX,
    y: screenY / scale - offsetY,
  };
}

function toScreen(worldX, worldY) {
  return {
    x: (worldX + offsetX) * scale,
    y: (worldY + offsetY) * scale,
  };
}

// Dessin
function draw() {
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(offsetX, offsetY);

  // Connexions
  ctx.strokeStyle = "rgba(0, 255, 255, 0.2)";
  ctx.lineWidth = 1.5;
  nodes.forEach((node) => {
    node.connections.forEach((targetId) => {
      const target = nodes.find((n) => n.id === targetId);
      if (target) {
        const highlight =
          focusedNode &&
          (focusedNode.id === node.id || focusedNode.id === target.id);

        if (highlight) {
          ctx.strokeStyle = "rgba(0, 255, 255, 0.6)";
          ctx.lineWidth = 2;
          ctx.shadowBlur = 10;
          ctx.shadowColor = "rgba(0, 255, 255, 0.5)";
        } else {
          ctx.strokeStyle = "rgba(0, 255, 255, 0.2)";
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
        }

        ctx.beginPath();
        ctx.moveTo(node.x, node.y);
        ctx.lineTo(target.x, target.y);
        ctx.stroke();
      }
    });
  });
  ctx.shadowBlur = 0;

  // Nœuds
  nodes.forEach((node) => {
    const pos = { x: node.x, y: node.y };
    const baseRadius = node.isMother ? 35 : 25;

    // Effet de zoom smooth au survol/focus - SÉPARÉ
    let targetZoom = 1; // Taille normale par défaut

    if (node === focusedNode) {
      // CLIC (focus) : zoom très gros
      targetZoom = ZOOM_FOCUS;
    } else if (node === hoveredNode) {
      // SURVOL (hover) : zoom léger
      targetZoom = ZOOM_HOVER;
    }

    const zoomScale = updateNodeZoom(node, targetZoom);
    const radius = baseRadius * zoomScale;

    const color = categories[node.category].color;

    const dimmed =
      focusedNode &&
      focusedNode.id !== node.id &&
      !focusedNode.connections.includes(node.id) &&
      !node.connections.includes(focusedNode.id);

    // Mode 1: Image dans une sphère (si imageInSphere: true)
    if (
      node.imageInSphere &&
      node.imageUrl &&
      nodeImages[node.id] &&
      imageLoadingStatus[node.id] === "loaded"
    ) {
      ctx.save();

      // Créer le masque circulaire
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
      ctx.clip(); // L'image sera rognée par ce cercle

      // Dessiner le fond du cercle d'abord
      if (node === hoveredNode || node === focusedNode) {
        ctx.fillStyle = color;
        ctx.shadowBlur = 25;
        ctx.shadowColor = color;
      } else if (dimmed) {
        ctx.fillStyle = "rgba(100, 100, 120, 0.3)";
      } else {
        const gradient = ctx.createRadialGradient(
          pos.x,
          pos.y,
          0,
          pos.x,
          pos.y,
          radius
        );
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color + "99");
        ctx.fillStyle = gradient;
      }
      ctx.fillRect(pos.x - radius, pos.y - radius, radius * 2, radius * 2);
      ctx.shadowBlur = 0;

      // Dessiner l'image qui REMPLIT le cercle (sera rognée)
      const baseDimensions = getImageDisplayDimensions(node);
      if (baseDimensions) {
        // Calculer les dimensions pour que l'image remplisse le cercle
        const diameter = radius * 2;
        const aspectRatio = baseDimensions.width / baseDimensions.height;

        let imgWidth, imgHeight;
        if (aspectRatio > 1) {
          // Image paysage : on base sur la hauteur
          imgHeight = diameter;
          imgWidth = diameter * aspectRatio;
        } else {
          // Image portrait ou carrée : on base sur la largeur
          imgWidth = diameter;
          imgHeight = diameter / aspectRatio;
        }

        if (dimmed) {
          ctx.globalAlpha = 0.3;
        }

        ctx.drawImage(
          nodeImages[node.id],
          pos.x - imgWidth / 2,
          pos.y - imgHeight / 2,
          imgWidth,
          imgHeight
        );
      }

      ctx.restore(); // Retire le clip

      // Dessiner le contour du cercle PAR-DESSUS
      ctx.save();
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = dimmed ? "rgba(150, 150, 170, 0.4)" : "#ffffff";
      ctx.lineWidth = node.isMother ? 2.5 : 2;
      if (node === hoveredNode || node === focusedNode) {
        ctx.shadowBlur = 15;
        ctx.shadowColor = color;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.restore();

      // Texte (masqué au hover, sauf ateliers toujours visibles)
      if (!dimmed && node !== hoveredNode) {
        ctx.fillStyle = "#ffffff";
        ctx.font = node.isMother
          ? 'bold 14px "Space Grotesk"'
          : '12px "Space Grotesk"';
        ctx.textAlign = "center";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
        ctx.fillText(node.title, pos.x, pos.y + radius + 22);
        ctx.shadowBlur = 0;
      }
      // Toujours afficher les titres des ateliers
      else if (node.category === "ateliers" && node !== hoveredNode) {
        ctx.fillStyle = "#ffffff";
        ctx.font = node.isMother
          ? 'bold 14px "Space Grotesk"'
          : '12px "Space Grotesk"';
        ctx.textAlign = "center";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
        ctx.fillText(node.title, pos.x, pos.y + radius + 22);
        ctx.shadowBlur = 0;
      }
    }
    // Mode 2: Image seule (mode actuel)
    else if (
      node.imageUrl &&
      nodeImages[node.id] &&
      imageLoadingStatus[node.id] === "loaded"
    ) {
      const baseDimensions = getImageDisplayDimensions(node);
      if (baseDimensions) {
        // Appliquer le zoom aux dimensions de l'image
        const dimensions = {
          width: baseDimensions.width * zoomScale,
          height: baseDimensions.height * zoomScale,
        };

        ctx.save();
        if (node === hoveredNode || node === focusedNode) {
          ctx.shadowBlur = 25;
          ctx.shadowColor = color;
        } else if (dimmed) {
          ctx.globalAlpha = 0.3;
        }
        ctx.drawImage(
          nodeImages[node.id],
          pos.x - dimensions.width / 2,
          pos.y - dimensions.height / 2,
          dimensions.width,
          dimensions.height
        );
        ctx.restore();

        // Texte pour image
        // Texte pour image (masqué au hover)
        if (!dimmed && node !== hoveredNode) {
          ctx.fillStyle = "#ffffff";
          ctx.font = node.isMother
            ? 'bold 14px "Space Grotesk"'
            : '12px "Space Grotesk"';
          ctx.textAlign = "center";
          ctx.shadowBlur = 8;
          ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
          ctx.fillText(
            node.title,
            pos.x,
            pos.y + Math.max(dimensions.width, dimensions.height) / 2 + 22
          );
          ctx.shadowBlur = 0;
        }
      }
    } else {
      // Cercle (code original)
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);

      if (node === hoveredNode || node === focusedNode) {
        ctx.fillStyle = color;
        ctx.shadowBlur = 25;
        ctx.shadowColor = color;
      } else if (dimmed) {
        ctx.fillStyle = "rgba(100, 100, 120, 0.3)";
      } else {
        const gradient = ctx.createRadialGradient(
          pos.x,
          pos.y,
          0,
          pos.x,
          pos.y,
          radius
        );
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color + "99");
        ctx.fillStyle = gradient;
      }

      ctx.fill();
      ctx.shadowBlur = 0;

      // Contour
      ctx.strokeStyle = dimmed ? "rgba(150, 150, 170, 0.4)" : "#ffffff";
      ctx.lineWidth = node.isMother ? 2.5 : 2;
      if (node === hoveredNode || node === focusedNode) {
        ctx.shadowBlur = 15;
        ctx.shadowColor = color;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Texte (masqué au hover, sauf ateliers toujours visibles)
      if (!dimmed && node !== hoveredNode) {
        ctx.fillStyle = "#ffffff";
        ctx.font = node.isMother
          ? 'bold 14px "Space Grotesk"'
          : '12px "Space Grotesk"';
        ctx.textAlign = "center";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
        ctx.fillText(node.title, pos.x, pos.y + radius + 22);
        ctx.shadowBlur = 0;
      }
      // Toujours afficher les titres des ateliers
      else if (node.category === "ateliers" && node !== hoveredNode) {
        ctx.fillStyle = "#ffffff";
        ctx.font = node.isMother
          ? 'bold 14px "Space Grotesk"'
          : '12px "Space Grotesk"';
        ctx.textAlign = "center";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
        ctx.fillText(node.title, pos.x, pos.y + radius + 22);
        ctx.shadowBlur = 0;
      }
    }
  });

  ctx.restore();
}

// Trouver nœud sous curseur
function findNodeAt(x, y) {
  const world = toWorld(x, y);
  return nodes.find((node) => {
    const dx = node.x - world.x;
    const dy = node.y - world.y;
    const radius = node.isMother ? 35 : 25;
    return Math.sqrt(dx * dx + dy * dy) < radius;
  });
}

// Gestion des événements souris
canvas.addEventListener("mousedown", (e) => {
  // Réinitialiser le timer d'interaction
  lastInteractionTime = Date.now();
  autoRecenterActive = false;

  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const node = findNodeAt(x, y);

  if (node) {
    // Ignorer les clics sur les participants (pas de détails disponibles)
    if (node.category === "participants") {
      // Ne rien faire pour les participants
      isDraggingCanvas = false;
      return;
    }

    // Clic sur un nœud : afficher détails + activer champ de force
    showDetail(node);

    // Activer le champ de force
    forceFieldActive = true;
    forceFieldX = node.x;
    forceFieldY = node.y;
    forceFieldTimer = FORCE_FIELD_DURATION;

    isDraggingCanvas = false;
  } else {
    // Clic sur le fond : fermer le panneau de détail s'il est ouvert
    if (detailPanel.classList.contains("active")) {
      closeDetail();
    }

    // Fermer le menu infos s'il est ouvert
    const infoMenu = null // géré par React state;
    if (infoMenu && infoMenu.classList.contains("active")) {
      toggleInfoMenu();
    }

    // Permettre le déplacement du canvas
    isDraggingCanvas = true;
    canvasContainer.classList.add("dragging");
  }

  lastMouseX = x;
  lastMouseY = y;
});

canvas.addEventListener("mousemove", (e) => {
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  if (isDraggingCanvas) {
    // Réinitialiser le timer d'interaction pendant le drag
    lastInteractionTime = Date.now();
    autoRecenterActive = false;

    // Déplacement du canvas
    const dx = x - lastMouseX;
    const dy = y - lastMouseY;
    offsetX += dx / scale;
    offsetY += dy / scale;
  } else {
    // Hover
    const node = findNodeAt(x, y);
    hoveredNode = node;

    if (node) {
      canvasContainer.classList.add("hovering");
      tooltip.textContent = node.title;
      tooltip.style.left = e.clientX + 15 + "px";
      tooltip.style.top = e.clientY + 15 + "px";
      tooltip.classList.add("visible");
    } else {
      canvasContainer.classList.remove("hovering");
      tooltip.classList.remove("visible");
    }
  }

  lastMouseX = x;
  lastMouseY = y;
});

canvas.addEventListener("mouseup", () => {
  isDraggingCanvas = false;
  canvasContainer.classList.remove("dragging");
});

canvas.addEventListener("mouseleave", () => {
  tooltip.classList.remove("visible");
  hoveredNode = null;
});

// Zoom avec molette
canvas.addEventListener("wheel", (e) => {
  e.preventDefault();

  // Réinitialiser le timer d'interaction
  lastInteractionTime = Date.now();
  autoRecenterActive = false;

  const rect = canvas.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  const worldBefore = toWorld(mouseX, mouseY);
  const delta = e.deltaY > 0 ? 0.9 : 1.1;
  scale *= delta;
  scale = Math.max(0.3, Math.min(3, scale));

  const worldAfter = toWorld(mouseX, mouseY);
  offsetX += worldAfter.x - worldBefore.x;
  offsetY += worldAfter.y - worldBefore.y;
});

// Affichage détails
function showDetail(node) {
  focusedNode = node;
  focusedNodeRef.current = node;

  const connectedNodes = node.connections
    .map((id) => nodes.find((n) => n.id === id))
    .filter((n) => n);

  const content = detailPanel.querySelector("#detail-content");

  // ========== Construire le contenu média ==========
  let mediaHTML = "";

  // Images
  if (node.detailImages && node.detailImages.length > 0) {
    mediaHTML += '<div class="detail-media">';
    node.detailImages.forEach((img) => {
      mediaHTML += `<img src="${img.src}" alt="${
        img.alt || node.title
      }" class="detail-image">`;
      if (img.caption) {
        mediaHTML += `<p class="detail-image-caption">${img.caption}</p>`;
      }
    });
    mediaHTML += "</div>";
  }

  // Vidéos
  if (node.detailVideos && node.detailVideos.length > 0) {
    mediaHTML += '<div class="detail-media">';
    node.detailVideos.forEach((video) => {
      mediaHTML += `
        <video controls class="detail-video">
          <source src="${video.src}" type="${video.type || "video/mp4"}">
          Votre navigateur ne supporte pas la vidéo.
        </video>`;
      if (video.caption) {
        mediaHTML += `<p class="detail-video-caption">${video.caption}</p>`;
      }
    });
    mediaHTML += "</div>";
  }

  // Audio - DEBUG
  console.log("Node detailAudios:", node.detailAudios);
  if (node.detailAudios && node.detailAudios.length > 0) {
    console.log("Generating audio HTML...");
    mediaHTML += '<div class="detail-media">';
    node.detailAudios.forEach((audio) => {
      console.log("Audio src:", audio.src);
      mediaHTML += `
      <audio controls class="detail-audio">
        <source src="${audio.src}" type="${audio.type || "audio/mpeg"}">
        Votre navigateur ne supporte pas l'audio.
      </audio>`;
      if (audio.caption) {
        mediaHTML += `<p class="detail-audio-caption">${audio.caption}</p>`;
      }
    });
    mediaHTML += "</div>";
    console.log("Audio HTML generated");
  }

  // ========== Générer le HTML complet ==========

  // Trouver l'atelier d'origine pour les créations
  let workshopInfo = "";
  if (node.category === "créations") {
    const relatedWorkshop = connectedNodes.find(
      (n) => n.category === "ateliers"
    );
    if (relatedWorkshop) {
      workshopInfo = `
        <div style="display:flex; align-items:center; gap:0.4rem; margin-top:0.2rem;">
          <span style="color:#b794f6; font-size:1.1rem; font-weight:600; text-transform:uppercase; letter-spacing:0.08em;">Atelier :</span>
          <span style="color:#e8e4f5; font-size:1.2rem;">${relatedWorkshop.title}</span>
        </div>
      `;
    }
  }

  // Trouver les participant·es connecté·es (pour tous les types d'éléments)
  let participantsInfo = "";
  const relatedParticipants = connectedNodes.filter(
    (n) => n.category === "participants"
  );
  if (relatedParticipants.length > 0) {
    const participantsList = relatedParticipants.map((p) => p.title).join(", ");
    participantsInfo = `
      <div style="display:flex; align-items:center; gap:0.4rem;">
        <span style="color:#b794f6; font-size:1.1rem; font-weight:600; text-transform:uppercase; letter-spacing:0.08em;">Par :</span>
        <span style="color:#e8e4f5; font-size:1.2rem;">${participantsList}</span>
      </div>
    `;
  }

  content.innerHTML = `
                <div style="margin-bottom:1.5rem; padding-bottom:1rem; border-bottom:1px solid rgba(0,255,255,0.15);">
                    <div style="font-size:1.8rem; font-weight:600; color:#00ffff; margin-bottom:0.8rem; text-shadow:0 0 15px rgba(0,255,255,0.4);">${node.title}</div>
                    <div style="display:flex; flex-direction:row; align-items:center; gap:1rem; margin-bottom:0.4rem;">
                        <div style="display:inline-block; padding:0.3rem 0.9rem; background:rgba(0,255,255,0.1); border:1px solid #00ffff; font-size:1.1rem; letter-spacing:0.1em; text-transform:uppercase; color:#00ffff;">${categories[node.category].name}</div>
                        ${participantsInfo}
                    </div>
                    ${workshopInfo}
                </div>
                
                ${mediaHTML}
                
                ${
                  node.description
                    ? `
                <div class="detail-section">
                    <div class="detail-section-title">Description</div>
                    <div class="detail-description">${node.description}</div>
                </div>
                `
                    : ""
                }
                
                ${
                  connectedNodes.length > 0
                    ? `
                    <div class="detail-section">
                    </div>
                `
                    : ""
                }
            `;

  // ========== Adapter la taille du panneau ==========
  adaptPanelSize(node);

  detailPanel.classList.add("active");
}

// Fonction pour adapter la taille du panneau selon le contenu
function adaptPanelSize(node) {
  const panel = detailPanel;

  // Détecter l'orientation dominante du contenu
  let isLandscape = false;

  // Si au moins une image paysage
  if (
    node.detailImages &&
    node.detailImages.some((img) => img.orientation === "landscape")
  ) {
    isLandscape = true;
  }
  // Si au moins une vidéo paysage
  if (
    node.detailVideos &&
    node.detailVideos.some((video) => video.orientation === "landscape")
  ) {
    isLandscape = true;
  }

  // Appliquer le mode
  panel.classList.remove("portrait-mode", "landscape-mode");
  panel.classList.add(isLandscape ? "landscape-mode" : "portrait-mode");
}

function closeDetail() {
  focusedNodeRef.current = null;
  // Arrêter toutes les vidéos en cours de lecture
  const videos = detailPanel.querySelectorAll("video");
  videos.forEach((video) => {
    video.pause();
    video.currentTime = 0;
  });

  // Arrêter tous les audios en cours de lecture
  const audios = detailPanel.querySelectorAll("audio");
  audios.forEach((audio) => {
    audio.pause();
    audio.currentTime = 0;
  });

  detailPanel.classList.remove("active");
  setTimeout(() => {
    focusedNode = null;
  }, 400);
}

function navigateToNode(id) {
  const node = nodes.find((n) => n.id === id);
  if (node) {
    showDetail(node);

    // Centrer sur le nœud
    offsetX = canvas.width / 2 / scale - node.x;
    offsetY = canvas.height / 2 / scale - node.y;

    // Activer le champ de force
    forceFieldActive = true;
    forceFieldX = node.x;
    forceFieldY = node.y;
    forceFieldTimer = FORCE_FIELD_DURATION;
  }
}

// Contrôles
function zoomIn() {
  lastInteractionTime = Date.now();
  autoRecenterActive = false;

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const worldBefore = toWorld(centerX, centerY);
  scale *= 1.2;
  scale = Math.min(3, scale);
  const worldAfter = toWorld(centerX, centerY);
  offsetX += worldAfter.x - worldBefore.x;
  offsetY += worldAfter.y - worldBefore.y;
}

function zoomOut() {
  lastInteractionTime = Date.now();
  autoRecenterActive = false;

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const worldBefore = toWorld(centerX, centerY);
  scale *= 0.8;
  scale = Math.max(0.3, scale);
  const worldAfter = toWorld(centerX, centerY);
  offsetX += worldAfter.x - worldBefore.x;
  offsetY += worldAfter.y - worldBefore.y;
}

function resetView() {
  lastInteractionTime = Date.now();
  autoRecenterActive = false;

  scale = 1;
  offsetX = canvas.width / 2 / scale - 600;
  offsetY = canvas.height / 2 / scale - 350;
  closeDetail();
}

// Fermeture avec ESC (panneau de détail)
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    // Fermer le panneau de détail si ouvert
    if (detailPanel.classList.contains("active")) {
      closeDetail();
    }
  }
});

// Animation
function animate() {
  applyPhysics();
  draw();
  requestAnimationFrame(animate);
}

// ==================== MENU INFOS ====================

// Toggle du menu infos
function toggleInfoMenu() {
  const menu = null // géré par React state;
  menu.classList.toggle("active");

  // Réinitialiser le timer d'interaction pour éviter l'auto-recentrage
  lastInteractionTime = Date.now();
}

// Switch entre les onglets
function switchInfoTab(tabName) {
  // Désactiver tous les onglets
  document.querySelectorAll(".atlas-info-tab").forEach((tab) => {
    tab.classList.remove("active");
  });
  document.querySelectorAll(".atlas-info-tab-content").forEach((content) => {
    content.classList.remove("active");
  });

  // Activer l'onglet sélectionné
  event.target.classList.add("active");
  document.getElementById("atlas-info-tab-" + tabName).classList.add("active");
}

// Fermeture avec ESC (menu infos)
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    const infoMenu = null // géré par React state;
    if (infoMenu.classList.contains("active")) {
      toggleInfoMenu();
    }
  }
});

// Initialisation
offsetX = canvas.width / 2 / scale - 600;
offsetY = canvas.height / 2 / scale - 350;
animate();

    // Écouter la fermeture depuis le bouton React
    const onClose = () => { closeDetail(); };
    document.addEventListener('atlas-close-detail', onClose);

    // Cleanup
    return () => {
      document.removeEventListener('atlas-close-detail', onClose);
      cancelAnimationFrame(window._atlasAnimId);
    };
  }, []);

  return (
    <div className="atlas-page">
{/* Bouton retour Vestibule */}
      <button className="atlas-back-btn" onClick={onBack} aria-label="Retour au vestibule">
        <svg viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="24">
          <polygon points="32,8 8,30 32,52" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
        <span>Vestibule</span>
      </button>

      {/* Bouton infos bas droite — Atlas */}
      <button className="qp-btn-infos" onClick={() => setInfoOpen(o => !o)}>infos</button>

      {/* Popup infos Atlas */}
      {infoOpen && (
        <div className="qp-infos-overlay" onClick={() => setInfoOpen(false)}>
          <div className="qp-infos-popup" onClick={e => e.stopPropagation()}>
            <button className="qp-infos-close" onClick={() => setInfoOpen(false)}>✕</button>

            <p className="qp-infos-category">Atelier de pratiques amateurs&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;16 - 26 ans</p>
            <h2 className="qp-infos-title">&nbsp;&nbsp;Fiction &amp; Mondes multiples</h2>
            <p className="qp-infos-subtitle"> &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;&nbsp;— s'abreuver à l'open source</p>

            <div className="qp-infos-block qp-infos-block--tight">
              <p>Atelier mené par l'artiste chercheure et compositrice Elise Voët, enseignante à l'ESAD Orléans en installation multimédia  &nbsp; &nbsp; &nbsp; →&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="qp-infos-link">https://elisevoet.space/</span> &nbsp; @_elise_voet_</p>
            </div>

            <div className="qp-infos-divider" />

            <div className="qp-infos-block">
              <p>Cet atlas présente des exemples d'ateliers et de créations sur le début de l'année.</p>
            </div>

            <div className="qp-infos-divider" />

            <div className="qp-infos-block">
              <p><strong>F.M.M. c'est la réunion d'une pratique transdisciplinaire des arts numériques, de l'audiovisuel et d'une volonté de créer de façon débridée des fragments de fiction.</strong></p>
              <p>L'année s'articule en petits ateliers (art génératif, mixage, montage, spatialisation sonore, surréalisme, etc) et la découverte d'un panel d'outils variés.<br/>Certaines séances explorent des aspects théoriques, techniques et critiques des arts numériques, de l'art contemporain et de la pop-culture.</p>
              <p>Tout en bénéficiant d'une pédagogie professionnalisante et critique, les participant·es sont introduit·es à des pratiques alternatives, artisanales en utilisant des outils accessibles portant les idéaux de la culture numérique libre.</p>
            </div>

            <div className="qp-infos-divider" />

            <div className="qp-infos-block">
              <p>Pour voir l'exposition virtuelle de fin d'année, cliquez en haut à gauche vers le Vestibule !
              <br/><br/>Si vous êtes intéressé·es, Elise se fera un plaisir de répondre à vos questions par mail : <span className="qp-infos-link">elise.voet@hotmail.fr</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Canvas constellation */}
      <div id="canvas-container" ref={containerRef} className="atlas-canvas-container">
        <canvas id="constellation-canvas" ref={canvasRef} />
      </div>

      {/* Panneau détail */}
      <div id="detail-panel" ref={detailRef} className="detail-panel">
        <button className="detail-close" onClick={() => {
          const panel = detailRef.current;
          if (panel) panel.classList.remove('active');
          // Réinitialiser focusedNode via ref
          if (focusedNodeRef.current !== undefined) {
            focusedNodeRef.current = null;
            // Accéder à focusedNode dans le scope du useEffect via un event custom
            document.dispatchEvent(new CustomEvent('atlas-close-detail'));
          }
        }}>X</button>
        <div id="detail-content"></div>
      </div>

      {/* Tooltip */}
      <div ref={tooltipRef} className="tooltip"></div>
    </div>
  );
};

export default Atlas;